<template>
  <div class="flex justify-center mt-3">
    <!-- Hidden div where Google renders its native button (handles popup flow) -->
    <div id="google-signin-hidden-btn" style="display: none;"></div>
    <button
      type="button"
      @click="signInWithGoogle"
      class="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
      style="width: 300px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif;"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style="width: 18px; height: 18px; flex-shrink: 0;">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        <path fill="none" d="M0 0h48v48H0z"/>
      </svg>
      <span>Sign in with Google</span>
    </button>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  data() {
    return {
      loadingMessage: 'Verifying account',
    };
  },

  mounted() {
    this.loadGoogleSDK();
  },

  methods: {
    loadGoogleSDK() {
      if (document.getElementById('google-gsi-script')) {
        this.initGoogle();
        return;
      }
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => this.initGoogle();
      document.head.appendChild(script);
    },

    initGoogle() {
      if (!window.google || !window.google.accounts) {
        setTimeout(() => this.initGoogle(), 200);
        return;
      }
      window.google.accounts.id.initialize({
        client_id: this.google_client_id,
        callback: this.handleGoogleCredential,
      });
      // Render a hidden native Google button so clicking it opens a proper
      // OAuth popup window — unaffected by One Tap cooldown after dismissal.
      window.google.accounts.id.renderButton(
        document.getElementById('google-signin-hidden-btn'),
        { theme: 'outline', size: 'large' }
      );
    },

    signInWithGoogle() {
      if (!window.google || !window.google.accounts) {
        this.$moshaToast('Google Sign-In is not available yet. Please try again.', { type: 'danger' });
        return;
      }
      // Programmatically click Google's native rendered button which opens
      // a real popup — this works even after the user dismisses One Tap.
      const nativeBtn = document.querySelector('#google-signin-hidden-btn div[role="button"]');
      if (nativeBtn) {
        nativeBtn.click();
      } else {
        window.google.accounts.id.prompt();
      }
    },

    async handleGoogleCredential(response) {
      Swal.fire({
        title: 'Please wait...',
        html: '<small>Verifying Google account</small>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const res = await axios({
          method: 'post',
          url: this.baseAPIURL + '/apiv3/googleCallback',
          data: {
            token: response.credential,
            device_identifier: 'TAPWEBIDENTIFIER',
            device_type: 'WEB',
            device_info: 'BROWSER',
            location: '',
            ip_public: '',
          },
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (res.data.data.result === 'success') {
          this.loadingMessage = 'Loading theme';
          Swal.update({ html: `<small>${this.loadingMessage}</small>` });

          const themeData = new FormData();
          themeData.append('email', res.data.data.email);

          const themeResponse = await axios({
            method: 'post',
            url: this.baseAPIURL + '/apiv3/theme.json',
            data: themeData,
            headers: {
              auth: res.data.data.token,
              'Content-Type': 'multipart/form-data',
            },
          });

          this.$store.dispatch('verify', {
            email: res.data.data.email,
            token: res.data.data.token,
            user_guid: res.data.data.guid,
          });

          this.$store.dispatch('updateNewLoad', true);
          Swal.close();
          this.$store.dispatch('updateTheme', themeResponse.data.theme[0].web_main_page_icon);

          res.data.data.data.book_code.map((item, index) => {
            if (index === 0) {
              if (this.$store.getters.selected_book === '') {
                this.$store.dispatch('activeBook', item.code);
                this.$store.dispatch('activeBookDesc', item.description);
                this.$store.dispatch('setPages', item.pages);
              }
              this.$store.dispatch('setBooks', item.code);
              this.$store.dispatch('setActiveBooks', item.code);
              this.$store.dispatch('setSelectedBookModDate', item.modified);
            } else {
              this.$store.dispatch('setBooks', item.code);
              this.$store.dispatch('setActiveBooks', item.code);
              this.$store.dispatch('setSelectedBookModDate', item.modified);
            }

            if (/@cloudstaff\.com\s*$/.test(res.data.data.email)) {
              this.$store.dispatch('setCS', true);
            } else {
              this.$store.dispatch('setCS', false);
            }
          });

          this.$moshaToast('Welcome!', { type: 'success' });
          this.$router.push('/dashboard');
        } else {
          Swal.close();
          this.$moshaToast('Google sign-in failed. Please try again.', { type: 'danger' });
        }
      } catch {
        Swal.close();
        this.$moshaToast('Google sign-in failed. Please try again.', { type: 'danger' });
      }
    },
  },
};
</script>
